/**
 * Build Mode Selector - Choose between Build Manually and Create with FunnelAssist
 * 
 * Build Manually: User creates structure with FunnelAssist help at each step
 * Create with FunnelAssist: AI generates complete funnel draft for review
 */

import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Pencil, 
  Sparkles, 
  ArrowRight,
  CheckCircle2
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function BuildModeSelector({ open, onClose, onSelectMode }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl glass border-white/10">
        <DialogHeader>
          <DialogTitle className="text-xl text-slate-100">Create a Funnel</DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-4 py-4">
          {/* Build Manually */}
          <button
            className={cn(
              "flex flex-col p-6 rounded-xl border-2 text-left transition-all",
              "border-white/10 hover:border-slate-600/50 hover:bg-slate-800/30",
              "focus:outline-none focus:ring-2 focus:ring-slate-600"
            )}
            onClick={() => onSelectMode("manual")}
            data-testid="manual-build-btn"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-lg bg-slate-700/30">
                <Pencil className="h-6 w-6 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-100">Build Manually</h3>
            </div>
            
            <p className="text-sm text-slate-200/70 mb-4">
              Create your funnel step-by-step with full control.
            </p>
            
            <p className="text-xs text-slate-200/60 mb-3">
              FunnelAssist is available to help refine questions, generate responses, and improve wording as you build.
            </p>
            
            <div className="space-y-2 mt-auto">
              <div className="flex items-center gap-2 text-xs text-slate-200/60">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
                <span>Polish question wording</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-200/60">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
                <span>Generate response options</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-200/60">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
                <span>Suggest acknowledgements</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-200/60">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
                <span>Improve conversational tone</span>
              </div>
            </div>
            
            <div className="flex items-center justify-end mt-4 text-sm text-slate-400">
              Start Building <ArrowRight className="ml-1 h-4 w-4" />
            </div>
          </button>
          
          {/* Create with FunnelAssist */}
          <button
            className={cn(
              "flex flex-col p-6 rounded-xl border-2 text-left transition-all",
              "border-indigo-500/30 hover:border-indigo-500/50 hover:bg-indigo-500/10",
              "focus:outline-none focus:ring-2 focus:ring-indigo-500",
              "relative overflow-hidden"
            )}
            onClick={() => onSelectMode("ai")}
            data-testid="ai-build-btn"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-lg bg-indigo-500/20">
                <Sparkles className="h-6 w-6 text-indigo-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-100">Create with FunnelAssist AI</h3>
            </div>
            
            <p className="text-sm text-slate-200/70 mb-4">
              Describe your funnel and FunnelAssist will build the structure for you.
            </p>
            
            <p className="text-xs text-slate-200/60 mb-3">
              You'll review and approve each step before publishing.
            </p>
            
            <div className="space-y-2 mt-auto">
              <div className="flex items-center gap-2 text-xs text-slate-200/60">
                <CheckCircle2 className="h-3.5 w-3.5 text-indigo-400" />
                <span>Full structure & branching</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-200/60">
                <CheckCircle2 className="h-3.5 w-3.5 text-indigo-400" />
                <span>Questions & response options</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-200/60">
                <CheckCircle2 className="h-3.5 w-3.5 text-indigo-400" />
                <span>Acknowledgement messages</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-200/60">
                <CheckCircle2 className="h-3.5 w-3.5 text-indigo-400" />
                <span>Exit nodes & optional booking</span>
              </div>
            </div>
            
            <div className="flex items-center justify-end mt-4 text-sm text-indigo-400">
              Generate <ArrowRight className="ml-1 h-4 w-4" />
            </div>
          </button>
        </div>
        
        <p className="text-xs text-slate-200/60 text-center">
          Note: Consent/TCPA language must always be entered manually, regardless of build mode.
        </p>
      </DialogContent>
    </Dialog>
  );
}
